import type { AttributionData } from "@repo/api/utils/attribution";
import {
  companyDomainMatchesEmail,
  normalizeCompanyDomain,
} from "@repo/common";
import { z } from "zod";

import {
  DEMO_BOOKING_CAL_LINK,
  SALES_BOOKING_CAL_LINK,
} from "@/app/(marketing)/_components/nav-links";

/**
 * What the visitor wants decides whose calendar they land on: AI training is
 * Zac's, everything else is sales'. The label is also what gets sent, so it is
 * capitalised for a field that is read on its own rather than inside a
 * sentence.
 */
export const RESEARCH_TYPES = [
  { id: "market", label: "Market research", calLink: SALES_BOOKING_CAL_LINK },
  {
    id: "ai-training",
    label: "AI training",
    calLink: DEMO_BOOKING_CAL_LINK,
  },
  { id: "user", label: "User research", calLink: SALES_BOOKING_CAL_LINK },
  { id: "other", label: "Other", calLink: SALES_BOOKING_CAL_LINK },
] as const;

/** "ethnographic fieldwork" typed into a box of its own reads as a mistake. */
function capitalise(value: string) {
  const trimmed = value.trim();
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

/**
 * The website is kept as typed so the form gives it back unchanged, and is
 * only valid when it reduces to a company's own domain: a LinkedIn or
 * Crunchbase page names the directory, not the company.
 */
export function isCompanyWebsite(value: string) {
  return normalizeCompanyDomain(value) !== null;
}

export const leadSchema = z.object({
  firstName: z.string().trim().min(1),
  lastName: z.string().trim().min(1),
  companyWebsite: z.string().trim().refine(isCompanyWebsite),
  researchType: z.enum(["market", "ai-training", "user", "other", ""]),
  otherResearchType: z.string(),
  referrer: z.string().trim().min(1),
});

export type Lead = z.infer<typeof leadSchema>;

/**
 * Each value rides inside the magic-link URL, so it is capped rather than
 * carried whole.
 */
const MAX_ATTRIBUTION_LENGTH = 256;

const attributionValue = z
  .string()
  .min(1)
  .max(MAX_ATTRIBUTION_LENGTH)
  .optional();

/**
 * Keys double as the Cal metadata names, which is what a webhook consumer
 * reads them by.
 */
const bookingAttributionSchema = z.object({
  utm_source: attributionValue,
  utm_medium: attributionValue,
  utm_campaign: attributionValue,
  utm_content: attributionValue,
  utm_term: attributionValue,
  landing_page: attributionValue,
  first_referrer: attributionValue,
});

export type BookingAttribution = z.infer<typeof bookingAttributionSchema>;

export const bookingDraftSchema = z.object({
  email: z.string().email(),
  lead: leadSchema.refine(
    (lead) =>
      lead.researchType !== "" &&
      (lead.researchType !== "other" || lead.otherResearchType.trim() !== ""),
  ),
  // A damaged attribution blob costs the attribution, not the lead's details.
  attribution: bookingAttributionSchema.optional().catch(undefined),
  expiresAt: z.number(),
});

export function researchTypeOf(lead: Lead) {
  return RESEARCH_TYPES.find((type) => type.id === lead.researchType);
}

/** The research type as the lead answered it, "Other" replaced by their own. */
export function researchTypeAnswerOf(lead: Lead) {
  const researchType = researchTypeOf(lead);
  return researchType?.id === "other"
    ? capitalise(lead.otherResearchType)
    : (researchType?.label ?? "");
}

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

/**
 * UTMs on the booking URL itself win as a set over the first-touch cookie, so
 * one campaign's source is never paired with another's medium. The landing
 * page and the external referrer only exist in the cookie.
 */
export function resolveBookingAttribution(
  query: Record<string, string | string[] | undefined>,
  firstTouch: AttributionData | null,
): BookingAttribution {
  const fromQuery = UTM_KEYS.map((key) => {
    const value = query[key];
    return [key, Array.isArray(value) ? value[0] : value] as const;
  });
  const utm = fromQuery.some(([, value]) => value?.trim())
    ? Object.fromEntries(fromQuery)
    : {
        utm_source: firstTouch?.utmSource,
        utm_medium: firstTouch?.utmMedium,
        utm_campaign: firstTouch?.utmCampaign,
        utm_content: firstTouch?.utmContent,
        utm_term: firstTouch?.utmTerm,
      };

  const attribution: BookingAttribution = {};
  for (const [key, value] of Object.entries({
    ...utm,
    landing_page: firstTouch?.entryPage,
    first_referrer: firstTouch?.referrer,
  })) {
    const trimmed = value?.trim();
    if (trimmed) {
      attribution[key as keyof BookingAttribution] = trimmed.slice(
        0,
        MAX_ATTRIBUTION_LENGTH,
      );
    }
  }
  return attribution;
}

/**
 * Everything the calendar is handed besides its layout. Answers go under the
 * booking-question slugs both Cal events define (Cal drops a param with no
 * matching question); the rest goes in as `metadata[...]`, which needs no
 * question and arrives on every webhook payload. The five standard UTM keys
 * are also sent bare, because Cal records those on the booking itself.
 */
export function bookingCalConfig({
  lead,
  email,
  attribution,
}: {
  lead: Lead;
  email: string;
  attribution?: BookingAttribution;
}): Record<string, string> {
  const companyDomain = normalizeCompanyDomain(lead.companyWebsite);
  const config: Record<string, string> = {
    name: `${lead.firstName} ${lead.lastName}`.trim(),
    email,
    company: companyDomain ?? lead.companyWebsite.trim(),
    "research-type": researchTypeAnswerOf(lead),
    referrer: lead.referrer.trim(),
    // `notes` is deliberately not sent. It is the event's own "Additional
    // notes" box, and leaving it empty gives the attendee somewhere to say
    // whatever the questions above did not ask for.
    "metadata[booking_source]": "researcher_booking_form",
  };
  if (companyDomain) {
    config["metadata[company_domain]"] = companyDomain;
    config["metadata[company_domain_matches_email]"] = String(
      companyDomainMatchesEmail(companyDomain, email),
    );
  }
  for (const [key, value] of Object.entries(attribution ?? {})) {
    if (!value) {
      continue;
    }
    config[`metadata[${key}]`] = value;
    if ((UTM_KEYS as readonly string[]).includes(key)) {
      config[key] = value;
    }
  }
  return config;
}
